import React, { useState, useEffect } from 'react';
import { FiX, FiClock, FiCalendar, FiUser, FiInfo, FiHash, FiChevronLeft, FiChevronRight, FiCheckCircle } from 'react-icons/fi';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const TicketSchedulerViewModal = ({ schedule, onClose, onNext, onPrevious, hasNext, hasPrevious, onEdit }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    useEffect(() => {
        const fetchHistory = async () => {
            if (!schedule) return;
            try {
                setLoading(true);
                const response = await api.get(`/ticket-scheduler/history/${schedule.schedule_id}`);
                setHistory(response.data);
                setCurrentPage(1); // Reset to page 1 for new schedule
            } catch (error) {
                console.error('Failed to fetch run history', error);
                toast.error('Failed to load run history');
            } finally {
                setLoading(false);
            }
        };
        fetchHistory();
    }, [schedule]);

    if (!schedule) return null;

    const formatTimeToAMPM = (timeStr) => {
        if (!timeStr) return '';
        return timeStr.split(',').map(t => {
            const parts = t.trim().split(':');
            if (parts.length < 2) return t;
            const h = parseInt(parts[0], 10);
            const m = parts[1];
            const ampm = h >= 12 ? 'PM' : 'AM';
            const displayHours = h % 12 || 12;
            return `${String(displayHours).padStart(2, '0')}:${m} ${ampm}`;
        }).join(', ');
    };

    const getRecurrenceText = (row) => {
        const interval = row.repeat_interval > 1 ? `Every ${row.repeat_interval} ` : '';
        const ampmTime = formatTimeToAMPM(row.start_time);
        const timeText = ampmTime ? ` at ${ampmTime}` : '';
        
        if (row.daily) return `${interval}Daily${timeText}`;
        if (row.weekly) {
            const days = (row.weekly || '').split(',').join(', ');
            return `${interval}Weekly on ${days}${timeText}`;
        }
        if (row.monthly) {
            const dates = (row.monthly || '').split(',').join(', ');
            return `${interval}Monthly on day ${dates}${timeText}`;
        }
        if (row.yearly) return `${interval}Yearly${timeText}`;
        return 'N/A';
    };

    return (
        <div className="fixed inset-0 z-[1000] flex justify-end bg-black/40 backdrop-blur-sm transition-all" onClick={onClose}>
            <div 
                className="w-full md:w-[850px] h-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-6 py-4 border-b bg-white flex flex-col gap-3 sticky top-0 z-20">
                    <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                            <h2 className="text-xl font-bold text-gray-900 truncate">
                                #{schedule.schedule_id} - {schedule.title}
                            </h2>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                                <span className="text-xs font-medium text-gray-500">Client: <span className="text-gray-900 uppercase">{schedule.client_short_code || schedule.client_display_name || 'N/A'}</span></span>
                                <span className="text-xs font-medium text-gray-500">Module: <span className="text-gray-900">{schedule.module_display_name || 'N/A'}</span></span>
                                <span className="text-xs font-medium text-gray-500">Status: <span className={Number(schedule.is_active) === 1 ? 'text-green-600 font-bold' : 'text-gray-500 font-bold'}>{Number(schedule.is_active) === 1 ? 'Active' : 'Inactive'}</span></span>
                                <span className="text-xs font-medium text-gray-500">Created By: <span className="text-gray-900">{schedule.created_by_name || 'System'}</span></span>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                             {/* Navigation Buttons Removed from top as requested */}
                            <button 
                                onClick={onClose}
                                className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-800 transition-all shadow-md active:scale-90"
                            >
                                <FiX size={16} />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Schedule Visualization / Details (Mocking the TicketForm look) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">Recurrence Configuration</label>
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center gap-3">
                                    <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                                        <FiCalendar size={20} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-gray-800">{getRecurrenceText(schedule)}</p>
                                        <p className="text-[10px] text-gray-500">Starts on {schedule.start_date}</p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">Assigned Resources</label>
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex flex-wrap gap-2">
                                    {(schedule.assigned_names || 'Unassigned').split(',').map((name, i) => (
                                        <span key={i} className="px-2 py-1 bg-white border border-gray-200 rounded text-xs font-medium text-gray-700 shadow-sm flex items-center gap-1.5">
                                            <FiUser size={10} className="text-gray-400" />
                                            {name.trim()}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">Execution Metrics</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                        <p className="text-[9px] text-gray-400 font-bold uppercase">Total Runs</p>
                                        <p className="text-lg font-bold text-gray-800">{schedule.execution_count || 0}</p>
                                    </div>
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                        <p className="text-[9px] text-gray-400 font-bold uppercase">Run Limit</p>
                                        <p className="text-lg font-bold text-gray-800">{Number(schedule.execution_limit) === 0 ? '∞' : schedule.execution_limit}</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">Last Activity</label>
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                    <p className="text-xs font-medium text-gray-600">Last run: <span className="text-gray-900 font-bold">{schedule.last_created_date || 'Never'}</span></p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Description Section */}
                    <div className="space-y-2">
                        <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1 flex items-center gap-1">
                            <FiInfo size={12} /> Instructions / Description
                        </label>
                        <div 
                            className="bg-white p-5 rounded-2xl border border-gray-200 text-sm text-gray-700 shadow-inner min-h-[100px] leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: schedule.description || '<em class="text-gray-400">No instructions defined for this schedule.</em>' }}
                        />
                    </div>

                    {/* History Section - Styling like the comments section in the image */}
                    <div className="space-y-4 pt-4">
                        <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2 uppercase tracking-wide">
                                <FiHash className="text-red-600" /> GENERATED TICKETS
                                <span className="bg-red-50 text-red-600 px-2 py-0.5 rounded-full text-[10px] font-bold border border-red-100">{history.length}</span>
                            </h3>
                        </div>

                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <div className="w-8 h-8 border-4 border-gray-100 border-t-red-600 rounded-full animate-spin"></div>
                                <p className="text-xs text-gray-400 mt-2">Fetching history...</p>
                            </div>
                        ) : history.length > 0 ? (
                            <div className="space-y-4">
                                <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                                    <table className="w-full text-left text-xs border-collapse">
                                        <thead>
                                            <tr className="bg-gray-50 border-b border-gray-200">
                                                <th className="px-4 py-3 font-bold text-gray-600 uppercase tracking-wider">ID</th>
                                                <th className="px-4 py-3 font-bold text-gray-600 uppercase tracking-wider">Title</th>
                                                <th className="px-4 py-3 font-bold text-gray-600 uppercase tracking-wider">Status</th>
                                                <th className="px-4 py-3 font-bold text-gray-600 uppercase tracking-wider">Created On</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {history.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((row, idx) => {
                                                const dateObj = new Date(row.created_date);
                                                const d = String(dateObj.getDate()).padStart(2, '0');
                                                const m = String(dateObj.getMonth() + 1).padStart(2, '0');
                                                const y = dateObj.getFullYear();
                                                const time = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
                                                
                                                return (
                                                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-4 py-3 font-bold text-main">#{row.ticket_master_id}</td>
                                                        <td className="px-4 py-3 font-medium text-gray-800 line-clamp-1 max-w-[300px]" title={row.title}>
                                                            {row.title}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded border border-blue-100 uppercase">
                                                                {row.status_name}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-gray-500 font-medium whitespace-nowrap">
                                                            {d}-{m}-{y} {time}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination for History Table */}
                                <div className="flex items-center justify-between px-2 py-3 mt-2 border-t border-gray-100">
                                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">
                                        Showing {history.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, history.length)} of {history.length}
                                    </p>
                                    <div className="flex items-center gap-1.5">
                                        <button 
                                            onClick={() => setCurrentPage(1)}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-[11px] font-bold text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                                        >
                                            First
                                        </button>
                                        
                                        {(() => {
                                            const totalPages = Math.ceil(history.length / itemsPerPage);
                                            // Show only a few pages around the current one if there are many
                                            let start = Math.max(1, currentPage - 1);
                                            let end = Math.min(totalPages, start + 2);
                                            if (end - start < 2) start = Math.max(1, end - 2);
                                            
                                            const pages = [];
                                            for (let i = start; i <= end; i++) {
                                                pages.push(
                                                    <button
                                                        key={i}
                                                        onClick={() => setCurrentPage(i)}
                                                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-bold transition-all shadow-sm ${
                                                            currentPage === i 
                                                            ? 'bg-red-600 text-white border border-red-600' 
                                                            : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {i}
                                                    </button>
                                                );
                                            }
                                            return pages;
                                        })()}

                                        <button 
                                            onClick={() => setCurrentPage(Math.ceil(history.length / itemsPerPage))}
                                            disabled={currentPage === Math.ceil(history.length / itemsPerPage) || history.length === 0}
                                            className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-[11px] font-bold text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                                        >
                                            Last
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl py-12 text-center group">
                                <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 text-gray-300 rounded-full mb-3 group-hover:scale-110 transition-transform">
                                    <FiHash size={24} />
                                </div>
                                <p className="text-xs text-gray-400 font-medium">This schedule hasn't executed yet.</p>
                                <p className="text-[10px] text-gray-300 italic mt-1 uppercase tracking-wider">Run history is empty</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-3 sm:p-4 border-t bg-gray-50 flex flex-wrap items-center justify-center sm:justify-end gap-2 sm:gap-3 sticky bottom-0 z-20">
                     <button 
                        onClick={onPrevious}
                        disabled={!hasPrevious}
                        className="flex-1 sm:flex-none px-3 py-2 bg-white text-gray-700 rounded-lg text-xs sm:text-sm font-bold border border-gray-200 hover:bg-gray-50 transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-sm"
                    >
                        <FiChevronLeft /> Prev
                    </button>
                    <button 
                        onClick={onNext}
                        disabled={!hasNext}
                        className="flex-1 sm:flex-none px-3 py-2 bg-white text-gray-700 rounded-lg text-xs sm:text-sm font-bold border border-gray-200 hover:bg-gray-50 transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-sm"
                    >
                        Next <FiChevronRight />
                    </button>
                    <div className="hidden sm:block border-l border-gray-300 mx-1 h-8 my-auto" />
                    
                    <button 
                        onClick={() => onEdit(schedule)}
                        className="w-full sm:w-auto px-6 py-2 bg-red-600 text-white rounded-lg text-sm font-bold shadow-lg hover:bg-red-700 transition-all active:scale-95 flex items-center justify-center gap-2"
                    >
                        <FiClock /> Edit Schedule
                    </button>

                    <button 
                        onClick={onClose}
                        className="w-full sm:w-auto px-8 py-2 bg-gray-900 text-white rounded-lg text-sm font-bold shadow-lg hover:bg-black transition-all active:scale-95"
                    >
                        Close View
                    </button>
                </div>
            </div>
            
            <style jsx>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #ddd;
                    border-radius: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #ccc;
                }
            `}</style>
        </div>
    );
};

export default TicketSchedulerViewModal;
